import { closeAllDropdowns, onCloseDropdowns } from '@/view/shared/Dropdown/dropdownEvents';
import { Dropdown as HeroDropdown } from '@heroui/react';
import { Lock } from 'lucide-react';
import type React from 'react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

export interface DropdownProps {
  classnames?: string;
  trigger?: React.ReactNode;
  actions: actionType[];
  onOpen?: () => void;
}

type actionType = {
  svg: React.JSX.Element;
  label: string;
  onClick: () => void;
  description?: string;
  disabled?: boolean;
  tooltip?: string;
};

export function Dropdown({ classnames, actions, onOpen, trigger }: Readonly<DropdownProps>) {
  const [isOpen, setIsOpen] = useState(false);
  const [hint, setHint] = useState<{ text: string; top: number; left: number } | null>(null);

  useEffect(() => onCloseDropdowns(() => setIsOpen(false)), []);

  const showHint = (text: string) => (event: React.MouseEvent<HTMLElement>) => {
    const row = event.currentTarget.getBoundingClientRect();
    setHint({ text, top: row.top + row.height / 2, left: row.right + 12 });
  };

  return (
    <div className={classnames || ''}>
      <HeroDropdown
        isOpen={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open);
          if (open) onOpen?.();
        }}
      >
        <HeroDropdown.Trigger
          onMouseDown={(event) => event.stopPropagation()}
          onPointerDown={(event) => event.stopPropagation()}
          onTouchStart={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        >
          {trigger ?? 'Options'}
        </HeroDropdown.Trigger>
        {isOpen && (
          <HeroDropdown.Popover className="z-80 rounded-lg" placement="bottom end">
            <HeroDropdown.Menu
              aria-label="Actions"
              className="text-body-sm"
              disabledKeys={actions.filter((a) => a.disabled).map((a) => a.label)}
              onAction={(key) => {
                const action = actions.find((a) => a.label === key);
                closeAllDropdowns();
                setTimeout(() => {
                  action?.onClick();
                }, 120);
              }}
            >
              {actions.map((action) => (
                <HeroDropdown.Item
                  key={action.label}
                  id={action.label}
                  textValue={action.label}
                  className="rounded-md data-hovered:bg-primary/5 data-disabled:opacity-40 data-disabled:pointer-events-auto data-disabled:cursor-not-allowed"
                >
                  <span
                    className="flex w-full items-center gap-2"
                    {...(action.tooltip
                      ? {
                          onMouseEnter: showHint(action.tooltip),
                          onMouseLeave: () => setHint(null),
                        }
                      : {})}
                  >
                    {action.svg}
                    <span className="flex flex-col">
                      <span>{action.label}</span>
                      {action.description && (
                        <span className="text-body-xs text-muted-foreground font-normal">
                          {action.description}
                        </span>
                      )}
                    </span>
                    {action.tooltip && (
                      <Lock size={11} className="ml-auto shrink-0 text-muted-foreground" />
                    )}
                  </span>
                </HeroDropdown.Item>
              ))}
            </HeroDropdown.Menu>
          </HeroDropdown.Popover>
        )}
      </HeroDropdown>
      {hint &&
        createPortal(
          <div
            role="tooltip"
            style={{ top: hint.top, left: hint.left }}
            className="pointer-events-none fixed z-90 max-w-60 -translate-y-1/2 rounded-md border border-border bg-surface px-2 py-1 text-body-xs text-muted-foreground shadow-md"
          >
            {hint.text}
          </div>,
          document.body,
        )}
    </div>
  );
}
